import { Callable, Id, Maybe, Optional } from '@vimeo/core/types/generic';

import { QnAManager } from './QnAManager';
import { disposeActiveSessionSubscription } from './QnAManager.operations';
import { TFirebaseReference, TFirebaseSnapshot } from '../../../../types/firebase';
import { IInteractionApplicationUser } from '../../../../types/interaction_room';
import { IQnASessionRecord } from '../../../../types/interaction_tools';
import {
  getRefChild,
  getRefValue,
  offRefEvent,
  onRefEvent,
} from '../../../../utils/firebase/cruds';
import { collectQuestionSessionInteractionErrors } from '../../../../utils/firebase/interaction_objects.validators';
import {
  transformBulkedQuestionReplies,
  transformQnASession,
} from '../../../../utils/firebase/transform';
import { omit } from '../../../../utils/general/object';
import { queryInteractionUserSync } from '../../../../utils/store/interaction';
import {
  checkErrorsAndTrack,
  ELiveErrorCategory,
  trackLiveError,
} from '../../../../utils/tracking';

/**
 * Hydrate moderation state for live event.
 */
export async function hydrateModerationState(
  manager: QnAManager,
  moderationRef: TFirebaseReference,
): Promise<void> {
  manager.log.info('Hydrating QNA moderation:', moderationRef.toString());

  try {
    const isEventModerated: boolean = Boolean(await getRefValue(manager.firebase, moderationRef));

    manager.log.info('Hydrated moderation state:', isEventModerated);
    manager.setContext({ isEventModerated: isEventModerated });
  } catch (error) {
    trackLiveError(error, {
      method: 'hydrateModerationState',
      category: ELiveErrorCategory.INTERACTION,
      data: {
        moderationRef: moderationRef.toString(),
      },
    });
  }
}

/**
 * Hydrate anonymous question state for live event.
 */
export async function hydrateAnonymousQuestionsState(
  manager: QnAManager,
  anonymousQuestionsRef: TFirebaseReference,
): Promise<void> {
  manager.log.info('Hydrating QNA anonymous question state:', anonymousQuestionsRef.toString());

  try {
    const isAnonymousQuestionsDisabled: boolean = Boolean(
      await getRefValue(manager.firebase, anonymousQuestionsRef),
    );

    manager.log.info('Hydrated anonymous question state:', isAnonymousQuestionsDisabled);
    manager.setContext({ isAnonymousQuestionsDisabled: isAnonymousQuestionsDisabled });
  } catch (error) {
    trackLiveError(error, {
      method: 'hydrateAnonymousQuestionsState',
      category: ELiveErrorCategory.INTERACTION,
      data: {
        anonymousQuestionsRef: anonymousQuestionsRef.toString(),
      },
    });
  }
}

/**
 * Hydrate active QNA information.
 * Get active session ID and then load information about it.
 */
export async function hydrateActiveSession(
  manager: QnAManager,
  sessionsRef: TFirebaseReference,
  activeSessionIdRef: TFirebaseReference,
): Promise<void> {
  manager.log.info('Hydrating active QNA session:', activeSessionIdRef.toString());

  try {
    const activeQnaId: Maybe<Id> = await getRefValue(manager.firebase, activeSessionIdRef);

    /**
     * Try to subscribe to active session and hydrate it if possible.
     */
    if (activeQnaId) {
      const sessionRef: TFirebaseReference = getRefChild(
        manager.firebase,
        sessionsRef,
        String(activeQnaId),
      );
      const sessionRepliesRef: TFirebaseReference = getRefChild(
        manager.firebase,
        manager.qnaRepliesSessionsRef as TFirebaseReference,
        String(activeQnaId),
      );

      const sessionRecord: Optional<IQnASessionRecord> = await loadActiveSessionRecord(
        manager,
        sessionRef,
      );

      if (sessionRecord) {
        const interactionUser: Optional<IInteractionApplicationUser> =
          queryInteractionUserSync(manager);
        const [
          nextSession,
          activeSessionQuestions,
          activeSessionPendingQuestions,
          activeSessionPinnedQuestionId,
        ] = transformQnASession(sessionRecord.id, sessionRecord, String(interactionUser?.id));

        manager.log.info('Hydrated active QNA session:', nextSession, sessionRef.toString());
        manager.setContext({
          activeSessionId: activeQnaId,
          activeSession: nextSession,
          activeSessionQuestions,
          activeSessionPendingQuestions,
          activeSessionPinnedQuestionId,
        });
      } else {
        manager.log.info('Hydrated active QNA id:', activeQnaId, activeSessionIdRef.toString());
        manager.setContext({ activeSessionId: activeQnaId });
      }

      subscribeDynamicallyToActiveSession(manager, sessionRef, sessionRepliesRef);
    }
  } catch (error) {
    trackLiveError(error, {
      method: 'hydrateActiveSession',
      category: ELiveErrorCategory.INTERACTION,
      data: {
        activeSessionIdRef: activeSessionIdRef.toString(),
      },
    });
  }
}

/**
 * Load active Q&A session by ref.
 */
export async function loadActiveSessionRecord(
  manager: QnAManager,
  sessionRef: TFirebaseReference,
): Promise<Optional<IQnASessionRecord>> {
  const nextQnaSession: Optional<IQnASessionRecord> = await getRefValue(
    manager.firebase,
    sessionRef,
  );

  if (
    nextQnaSession &&
    checkErrorsAndTrack({
      possibleErrors: collectQuestionSessionInteractionErrors(nextQnaSession),
      data: nextQnaSession,
      methodName: 'onActiveSessionValue',
    })
  ) {
    return nextQnaSession;
  } else {
    return null;
  }
}

/**
 * Subscribe to session and session replies by active refs.
 */
export function subscribeDynamicallyToActiveSession(
  manager: QnAManager,
  sessionRef: TFirebaseReference,
  sessionRepliesRef: TFirebaseReference,
): void {
  const onActiveSessionValue = (snapshot: TFirebaseSnapshot): void => {
    const nextQnaSession: Optional<IQnASessionRecord> = snapshot.val();

    /**
     * If got actual model, and it is valid, transform it and parse questions.
     * If no actual model / it has errors, reset in-memory store.
     */
    if (
      nextQnaSession &&
      checkErrorsAndTrack({
        possibleErrors: collectQuestionSessionInteractionErrors(nextQnaSession),
        data: nextQnaSession,
        methodName: 'onActiveSessionValue',
      })
    ) {
      const interactionUser: Optional<IInteractionApplicationUser> =
        queryInteractionUserSync(manager);
      const [
        nextSession,
        activeSessionQuestions,
        activeSessionPendingQuestions,
        activeSessionPinnedQuestionId,
      ] = transformQnASession(snapshot.key as string, nextQnaSession, String(interactionUser?.id));

      manager.setContext({
        activeSession: nextSession,
        activeSessionQuestions,
        activeSessionPendingQuestions,
        activeSessionPinnedQuestionId,
      });
    } else {
      manager.setContext({
        activeSession: null,
        activeSessionQuestions: {},
        activeSessionPendingQuestions: {},
        activeSessionPinnedQuestionId: null,
      });

      if (manager.qnaRepliesBuffer) {
        manager.qnaRepliesBuffer.resetValue();
      }
    }
  };

  const onQuestionReplyAdded = (snapshot: TFirebaseSnapshot): void => {
    if (manager.qnaRepliesBuffer) {
      manager.qnaRepliesBuffer.updateValue({
        [snapshot.key as string]: transformBulkedQuestionReplies(snapshot.val()),
      });
    } else {
      manager.log.warn('Question appeared without initialized buffer');
    }
  };

  const onQuestionReplyChanged = (snapshot: TFirebaseSnapshot): void => {
    if (manager.qnaRepliesBuffer) {
      manager.qnaRepliesBuffer.updateValue({
        [snapshot.key as string]: transformBulkedQuestionReplies(snapshot.val()),
      });
    } else {
      manager.log.warn('Question changed without initialized buffer');
    }
  };

  const onQuestionReplyRemoved = (snapshot: TFirebaseSnapshot): void => {
    if (manager.qnaRepliesBuffer) {
      manager.qnaRepliesBuffer.setValue(
        omit(manager.qnaRepliesBuffer.getValue(), snapshot.key as string),
      );
    } else {
      manager.log.warn('Question changed without initialized buffer');
    }
  };

  manager.log.info('Subscribing to active qna session:', sessionRef.toString());
  manager.qnaActiveSessionRef = sessionRef;

  onRefEvent(manager.firebase, sessionRef, 'value', onActiveSessionValue, 'active-session-value');
  onRefEvent(
    manager.firebase,
    sessionRepliesRef,
    'child_added',
    onQuestionReplyAdded,
    'active-session-reply-added',
  );
  onRefEvent(
    manager.firebase,
    sessionRepliesRef,
    'child_removed',
    onQuestionReplyRemoved,
    'active-session-reply-removed',
  );
  onRefEvent(
    manager.firebase,
    sessionRepliesRef,
    'child_changed',
    onQuestionReplyChanged,
    'active-session-reply-changed',
  );

  manager.activeSessionUnSubscribers.push(() => {
    offRefEvent(manager.firebase, sessionRef, 'value', onActiveSessionValue);
    offRefEvent(manager.firebase, sessionRepliesRef, 'child_added', onQuestionReplyAdded);
    offRefEvent(manager.firebase, sessionRepliesRef, 'child_removed', onQuestionReplyRemoved);
    offRefEvent(manager.firebase, sessionRepliesRef, 'child_changed', onQuestionReplyChanged);
  });
}

/**
 * Subscribe to active session changes, on change optionally subscribe to selected session.
 */
export function subscribeToActiveSessionMeta(
  manager: QnAManager,
  sessionsRef: TFirebaseReference,
  activeSessionIdRef: TFirebaseReference,
): Callable {
  const onActiveSessionChange = (snapshot: TFirebaseSnapshot<string>) => {
    const nextActiveSessionId: Maybe<Id> = snapshot.val();
    const previousActiveSessionId: Maybe<Id> = manager.context.activeSessionId;

    if (previousActiveSessionId === nextActiveSessionId) {
      return;
    }

    manager.log.info(
      'QnA active session changed:',
      previousActiveSessionId,
      '->',
      nextActiveSessionId,
    );

    if (previousActiveSessionId) {
      disposeActiveSessionSubscription(manager);
    }

    /**
     * Subscribe to next.
     */
    if (nextActiveSessionId) {
      manager.setContext({ activeSessionId: nextActiveSessionId });
      subscribeDynamicallyToActiveSession(
        manager,
        getRefChild(manager.firebase, sessionsRef, String(nextActiveSessionId)),
        getRefChild(
          manager.firebase,
          manager.qnaRepliesSessionsRef as TFirebaseReference,
          String(nextActiveSessionId),
        ),
      );
    }
  };

  onRefEvent(
    manager.firebase,
    activeSessionIdRef,
    'value',
    onActiveSessionChange,
    'active-session-id',
  );
  return () => offRefEvent(manager.firebase, activeSessionIdRef, 'value', onActiveSessionChange);
}

/**
 * Subscribe and observe event-level moderation state of QNA.
 */
export function subscribeToModerationState(
  manager: QnAManager,
  moderationStateRef: TFirebaseReference,
): Callable {
  const onModerationStateValue = (snapshot: TFirebaseSnapshot<boolean>) => {
    const nextIsEventModerated: boolean = Boolean(snapshot.val());
    const { isEventModerated } = manager.context;

    if (nextIsEventModerated !== isEventModerated) {
      manager.log.info(
        'QnA moderation state changed:',
        manager.context.isEventModerated,
        '->',
        nextIsEventModerated,
      );
      manager.setContext({ isEventModerated: nextIsEventModerated });
    }
  };

  onRefEvent(
    manager.firebase,
    moderationStateRef,
    'value',
    onModerationStateValue,
    'moderation-state',
  );
  return () => offRefEvent(manager.firebase, moderationStateRef, 'value', onModerationStateValue);
}

/**
 * Subscribe and observe event-level anonymous question state of QNA.
 */
export function subscribeToAnonymousQuestionsState(
  manager: QnAManager,
  anonymousQuestionsRef: TFirebaseReference,
): Callable {
  const onAnonymousQuestionsStateValue = (snapshot: TFirebaseSnapshot<boolean>) => {
    const nextIsAnonymousQuestionsDisabled: boolean = Boolean(snapshot.val());
    const { isAnonymousQuestionsDisabled } = manager.context;

    if (nextIsAnonymousQuestionsDisabled !== isAnonymousQuestionsDisabled) {
      manager.log.info(
        'QnA anonymous question state changed:',
        manager.context.isAnonymousQuestionsDisabled,
        '->',
        nextIsAnonymousQuestionsDisabled,
      );
      manager.setContext({ isAnonymousQuestionsDisabled: nextIsAnonymousQuestionsDisabled });
    }
  };

  onRefEvent(
    manager.firebase,
    anonymousQuestionsRef,
    'value',
    onAnonymousQuestionsStateValue,
    'anonymous-question-state',
  );
  return () =>
    offRefEvent(manager.firebase, anonymousQuestionsRef, 'value', onAnonymousQuestionsStateValue);
}
